import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { PointTopup } from "../api/models"
import { addEntityFetchReducers, EntityState } from "./entity"
import { logOut } from "./globalSlice"
import { addPaginatedCollectionReducers, PaginatedCollectionState, usePaginatedCollection } from "./paginatedCollection"
import { redeemCodeThunk, redeemResellerCodeThunk, redemptionsThunk, redemptionThunk } from "./redemptionsSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { useFetchableState } from "./fetchableState"

interface PointTopupsState extends EntityState<PointTopup>, PaginatedCollectionState {
    debug: string
}

const topupsInitialState: PointTopupsState = {
    ids: undefined,
    link: null,
    currentRequestId: undefined,
    lastFetched: undefined,
    error: undefined,
    entities: {},
    debug: "point topups state",
}


export const pointTopupsSlice = createSlice({
    name: 'pointTopups',
    initialState: topupsInitialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            PointTopup,
            { pointTopup: PointTopup },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.pointTopup,
            pointsTopupThunk
        )
        addPaginatedCollectionReducers<
            PointTopup,
            { pointTopups: PointTopup[], link: string | null },
            { reload: boolean, accessToken: string },
            PointTopupsState
        >(
            builder,
            (payload) => payload.pointTopups,
            pointsTopupsThunk,
            topupsReducerIntercept
        )        
        builder.addCase(redemptionThunk.fulfilled, (state, action) => {
            const { pointTopups } = action.payload
            pointTopups.forEach(pt => {
                state.entities[pt.id] = {
                    data: pt,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(redemptionsThunk.fulfilled, (state, action) => {
            const { pointTopups } = action.payload
            pointTopups.forEach(pt => {
                state.entities[pt.id] = {
                    data: pt,
                    lastFetched: Date.now()
                }
            })
        })
        // a code redemption and a reseller-code redemption side-load the same entities
        const onCodeRedeemed = (state: PointTopupsState, action: { payload: { pointTopups: PointTopup[] } }) => {
            const { pointTopups } = action.payload
            pointTopups.forEach(pt => {
                state.entities[pt.id] = {
                    data: pt,
                    lastFetched: Date.now()
                }
            })
        }
        builder.addCase(redeemCodeThunk.fulfilled, onCodeRedeemed)
        builder.addCase(redeemResellerCodeThunk.fulfilled, onCodeRedeemed)

        builder.addCase(logOut, (state) => {
            state = topupsInitialState;
        })
    },
})


export const pointsTopupThunk = createAsyncThunk('points/fetchPointsTopup', async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const currentRequestId = state.pointTopups.entities[id].currentRequestId
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.getPointTopup({ id, accessToken })
})

export const pointsTopupsThunk = createAsyncThunk('points/fetchPointsTopups', async ({ reload, accessToken }: { reload: boolean, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const currentRequestId = state.pointTopups.currentRequestId
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.listPointTopups({ includeExpired: true, link: state.topups.link, count: 10, accessToken })
})

const topupsReducerIntercept = (type: any, state: PointTopupsState, action: any) => {
    if (type === pointsTopupsThunk.fulfilled) {
        const pointTopups: PointTopup[] = action.payload.pointTopups
        pointTopups.forEach(pt => {
            const entity = state.entities[pt.id]
            if (entity?.currentRequestId !== undefined)
                return
            state.entities[pt.id] = {
                data: pt,
                lastFetched: Date.now()
            }
        })
    }
}

export const usePointTopup = (id: string, accessToken: string) => {
    return useFetchableState<PointTopup>({
        selector: (state: RootState) => state.pointTopups.entities[id],
        action: pointsTopupThunk({ id, accessToken })
    })
}

export const usePointTopups = (accessToken: string) => {
    return usePaginatedCollection({
        selector: (state: RootState) => state.pointTopups,
        moreAction: pointsTopupsThunk({ reload: false, accessToken }),
        reloadAction: pointsTopupsThunk({ reload: true, accessToken }),
    })
}

export default pointTopupsSlice.reducer
